import moment from "moment";
import { XMarkIcon } from "@heroicons/react/24/outline";

// Only the four known message_type keys are mapped. The backend enum can grow
// without a frontend release, so anything unknown falls back to the neutral
// accent rather than crashing or rendering an empty colour.
export const ANNOUNCEMENT_ACCENTS: Record<string, string> = {
    feature_launch: "#43BF63",
    monthly_update: "#43BF63",
    urgent_alert: "#E0413C",
    maintenance: "#E0A030",
};

export const ANNOUNCEMENT_NEUTRAL_ACCENT = "#17233A";

// The read-more link is green on every message type per the approved design —
// only the primary button is filled with the type's accent.
export const ANNOUNCEMENT_LINK_COLOR = "#43BF63";

export const getAnnouncementAccent = (messageType?: string | null): string =>
    (messageType ? ANNOUNCEMENT_ACCENTS[messageType] : undefined) ??
    ANNOUNCEMENT_NEUTRAL_ACCENT;

// Fallback kicker for the platform modal, which cannot read the admin-only
// /options labels. "feature_launch" -> "Feature Launch".
export const humanizeMessageType = (messageType?: string | null): string =>
    (messageType ?? "")
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\b\w/g, (character) => character.toUpperCase());

export interface AnnouncementModalViewProps {
    messageType: string;
    // Resolved display label for messageType (server label in the builder,
    // humanized key in the platform modal).
    kicker: string;
    headline: string;
    body: string;
    buttonLabel: string;
    linkText?: string | null;
    linkUrl?: string | null;
    imageUrl?: string | null;
    publishedAt?: string | null;
    isMobile?: boolean;
    // false for the builder's live preview: nothing is clickable and the
    // read-more link does not navigate.
    interactive?: boolean;
    // Sizing/scroll classes are the caller's call — the modal and the preview
    // need different widths and max heights.
    className?: string;
    onAction?: () => void;
    onLinkClick?: () => void;
    onClose?: () => void;
}

const AnnouncementModalView: React.FC<AnnouncementModalViewProps> = ({
    messageType,
    kicker,
    headline,
    body,
    buttonLabel,
    linkText,
    linkUrl,
    imageUrl,
    publishedAt,
    isMobile = false,
    interactive = true,
    className = "",
    onAction,
    onLinkClick,
    onClose,
}) => {
    const accent = getAnnouncementAccent(messageType);
    const showLink = Boolean(linkText && linkUrl);
    const formattedDate = publishedAt
        ? moment(publishedAt).format("MMM D, YYYY")
        : "";

    return (
        <div
            data-testid="announcement-card"
            className={`relative overflow-hidden bg-white dark:bg-gray-800 text-left shadow-2xl ${
                isMobile ? "rounded-t-[20px]" : "rounded-2xl"
            } ${className}`}
        >
            {isMobile && (
                <div
                    data-testid="announcement-grab-handle"
                    className="mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"
                />
            )}

            <button
                type="button"
                aria-label="Close"
                data-testid="announcement-close"
                tabIndex={interactive ? undefined : -1}
                onClick={interactive ? onClose : undefined}
                className="absolute top-3 right-3 z-10 flex h-[34px] w-[34px] items-center justify-center rounded-full bg-white/90 text-gray-600 shadow hover:bg-white dark:bg-gray-900/80 dark:text-gray-300"
            >
                <XMarkIcon className="h-5 w-5" />
            </button>

            {imageUrl && (
                <img
                    src={imageUrl}
                    alt=""
                    data-testid="announcement-hero"
                    className={
                        isMobile
                            ? "mx-4 mt-3 h-[150px] w-[calc(100%-32px)] rounded-xl object-cover"
                            : "h-[210px] w-full object-cover"
                    }
                />
            )}

            <div
                data-testid="announcement-accent-bar"
                className={`h-1 w-full ${imageUrl && isMobile ? "mt-3" : ""}`}
                style={{ backgroundColor: accent }}
            />

            <div className={isMobile ? "px-5 pt-5 pb-6" : "px-8 pt-7 pb-8"}>
                {/* Without a hero the close button overlaps this row, so the
                    date needs to clear it. */}
                <div
                    className={`flex items-center justify-between gap-3 ${
                        imageUrl ? "" : "pr-10"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: accent }}
                        />
                        <span
                            data-testid="announcement-kicker"
                            className="text-[11px] font-semibold uppercase tracking-[.13em]"
                            style={{ color: accent }}
                        >
                            {kicker}
                        </span>
                    </div>
                    {formattedDate && (
                        <span className="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
                            {formattedDate}
                        </span>
                    )}
                </div>

                <h2
                    className={`mt-3 font-bold leading-[1.25] text-heading dark:text-gray-100 ${
                        isMobile ? "text-[21px]" : "text-[25px]"
                    }`}
                >
                    {headline}
                </h2>

                <p className="mt-3 whitespace-pre-line text-[15px] leading-[1.65] text-gray-600 dark:text-gray-300">
                    {body}
                </p>

                <div
                    className={`mt-7 flex ${
                        isMobile
                            ? "flex-col items-stretch gap-3"
                            : "flex-row items-center gap-5"
                    }`}
                >
                    <button
                        type="button"
                        data-testid="announcement-action"
                        tabIndex={interactive ? undefined : -1}
                        onClick={interactive ? onAction : undefined}
                        className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white ${
                            isMobile ? "w-full" : ""
                        }`}
                        style={{ backgroundColor: accent }}
                    >
                        {buttonLabel}
                    </button>

                    {showLink &&
                        (interactive ? (
                            // onLinkClick closes the modal, so this anchor
                            // relies on HeadlessUI keeping the node mounted
                            // through its leave transition for the navigation
                            // to land. Zeroing that transition duration would
                            // break the link.
                            <a
                                href={linkUrl as string}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="announcement-link"
                                onClick={onLinkClick}
                                className={`text-sm font-semibold ${
                                    isMobile ? "text-center" : ""
                                }`}
                                style={{ color: ANNOUNCEMENT_LINK_COLOR }}
                            >
                                {linkText} &rarr;
                            </a>
                        ) : (
                            <span
                                data-testid="announcement-link"
                                className={`text-sm font-semibold ${
                                    isMobile ? "text-center" : ""
                                }`}
                                style={{ color: ANNOUNCEMENT_LINK_COLOR }}
                            >
                                {linkText} &rarr;
                            </span>
                        ))}
                </div>
            </div>
        </div>
    );
};

export default AnnouncementModalView;
